<template>
  <v-sheet
    class="px-3 mx-auto"
    color="transparent"
    max-width="960px"
    width="100%"
  >
    <v-overlay :value="isLoadingInsert">
      <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>

    <template v-if="isLoading">
      <v-card class="mt-6 mt-sm-7 form-card">
        <div class="d-flex align-center">
          <v-progress-circular indeterminate size="20" color="red" />

          <div class="text-h6 font-weight-medium ml-3">Carregando...</div>
        </div>
      </v-card>
    </template>

    <template v-else>
      <v-sheet class="fixed-top" color="#f7f8fa">
        <TitleFormWidget
          class="px-3 py-4"
          icon="map-marker-radius-outline"
          :title="title"
        />

        <v-divider />
      </v-sheet>

      <v-form @submit.prevent="submit" ref="form">
        <v-card class="form-card">
          <TitleFormWidget
            icon="alert-circle-outline"
            title="Informações Gerais"
            small
          />

          <div class="text-body-2 font-weight-bold mt-5 mb-1">
            Identificação
          </div>
          <div class="grid-list grid-list-4-columns">
            <TextFarmField class="span2" v-model="fazenda.nome" title="Nome" />

            <SelectFarmField
              v-model="fazenda.tipo_imovel"
              :items="propertyType"
              title="Tipo de imóvel"
              clearable
            />

            <SelectFarmField
              v-model="fazenda.cultura_desenv"
              title="Finalidade"
              :items="purpose"
              clearable
            />
          </div>

          <v-divider class="my-4" />

          <div class="text-body-2 font-weight-bold mb-1">Localização</div>
          <div class="grid-list grid-list-4-columns">
            <div class="grid-list grid-list-state-city">
              <SelectFarmField
                @change="updateState"
                @click:clear="fazenda.state_id = null"
                v-model="fazenda.state_id"
                :items="states"
                title="Estado"
                clearable
              />

              <SelectFarmField
                v-model="fazenda.city_id"
                no-data-text="Selecione um estado."
                :items="citiesByState"
                title="Cidade"
                clearable
              />
            </div>
          </div>

          <v-divider class="my-4" />

          <div class="text-body-2 font-weight-bold mb-1">
            Dimensões e negócio
          </div>
          <div class="grid-list grid-list-5-columns">
            <SelectFarmField
              v-model="fazenda.sistema_metrico"
              title="Unidade de área"
              :items="areaUnit"
              clearable
            />

            <TextFarmField
              v-model="fazenda.area_total"
              title="Área total"
              placeholder="Somente números"
              :suffix="formatAreaUnit(fazenda.sistema_metrico)"
              area
            />

            <TextFarmField
              v-model="fazenda.area_aberta"
              title="Área aberta"
              placeholder="Somente números"
              :suffix="formatAreaUnit(fazenda.sistema_metrico)"
              area
            />

            <SelectFarmField
              v-model="fazenda.tipo"
              :items="areaType"
              title="Negociação"
              clearable
            />

            <TextFarmField
              v-model="fazenda.valor_total"
              title="Valor total"
              placeholder="0,00"
              monetary
            />
          </div>
        </v-card>

        <v-card class="mt-6 mt-sm-8 form-card">
          <TitleFormWidget icon="map-plus" title="Mapa da área (KMZ)" small />

          <div class="mt-3">
            <KmzFileFarmField
              v-model="fazenda.kmz_novo"
              helper="Envie um arquivo no formato KMZ contendo o perímetro da área."
              accept=".kmz"
            />

            <PolygonMap
              v-if="fazenda.mapa && fazenda.mapa.length"
              class="mt-5"
              :coordinates="fazenda.mapa"
            />
          </div>
        </v-card>

        <v-card class="mt-6 mt-sm-8 form-card">
          <TitleFormWidget
            icon="text-box-multiple-outline"
            title="Benfeitorias"
            small
          />

          <TextareaFarmField v-model="fazenda.benfeitorias" class="mt-3" />
        </v-card>

        <v-card class="mt-6 mt-sm-8 form-card">
          <TitleFormWidget
            icon="text-box-multiple-outline"
            title="Informações adicionais"
            small
          />

          <TextareaFarmField
            v-model="fazenda.informacoes_diversas"
            class="mt-3"
          />
        </v-card>

        <v-card class="mt-6 mt-sm-8 form-card">
          <TitleFormWidget
            icon="text-box-multiple-outline"
            title="Observações Internas"
            small
          />

          <TextareaFarmField
            v-model="fazenda.internal_observations"
            class="mt-3"
          />
        </v-card>

        <v-card class="mt-6 mt-sm-8 form-card">
          <TitleFormWidget
            icon="image-outline"
            :title="
              'Fotos' +
              (fazenda.fotos_novos?.length > 0 || fazenda.fotos?.length > 0
                ? ` (${
                    fazenda.fotos_novos.length + (fazenda.fotos?.length || 0)
                  })`
                : '')
            "
            small
          />

          <FileFarmField
            @delete-files="deleteFiles"
            v-model="fazenda.fotos_novos"
            class="mt-3"
            :items="fazenda.fotos"
            table="fotos"
            helper="Envie as fotos da área."
            accept=".jpg,.jpeg,.png,.webp"
          />
        </v-card>

        <v-card class="mt-6 mt-sm-8 form-card">
          <TitleFormWidget
            icon="video-outline"
            :title="
              'Vídeos' +
              (fazenda.videos_novos?.length > 0 || fazenda.videos?.length > 0
                ? ` (${
                    fazenda.videos_novos.length + (fazenda.videos?.length || 0)
                  })`
                : '')
            "
            small
          />

          <FileFarmField
            @delete-files="deleteFiles"
            v-model="fazenda.videos_novos"
            :items="fazenda.videos"
            class="mt-3"
            table="videos"
            helper="Envie os vídeos da área."
            accept=".mp4"
          />
        </v-card>

        <v-card class="mt-6 mt-sm-8 form-card">
          <TitleFormWidget icon="file-outline" title="Documentos" small />

          <FileFarmField
            @delete-files="deleteFiles"
            v-model="fazenda.documentos_ambientais_novos"
            :items="fazenda.documentos_ambientais"
            class="mt-3"
            :title="
              'Documentos ambientais' +
              (fazenda.documentos_ambientais_novos?.length > 0 ||
              fazenda.documentos_ambientais?.length > 0
                ? ` (${
                    fazenda.documentos_ambientais_novos.length +
                    (fazenda.documentos_ambientais?.length || 0)
                  })`
                : '')
            "
            table="documentos_ambientais"
            helper="Envie os documentos ambientais da área."
            accept=".pdf"
          />

          <v-divider class="my-4" />

          <FileFarmField
            @delete-files="deleteFiles"
            v-model="fazenda.documentos_fundiarios_novos"
            :items="fazenda.documentos_fundiarios"
            :title="
              'Documentos fundiários' +
              (fazenda.documentos_fundiarios_novos?.length > 0 ||
              fazenda.documentos_fundiarios?.length > 0
                ? ` (${
                    fazenda.documentos_fundiarios_novos.length +
                    (fazenda.documentos_fundiarios?.length || 0)
                  })`
                : '')
            "
            table="documentos_fundiarios"
            helper="Envie os documentos fundiários da área."
            accept=".pdf"
          />

          <v-divider class="my-4" />

          <FileFarmField
            @delete-files="deleteFiles"
            v-model="fazenda.documentos_diversos_novos"
            :items="fazenda.documentos_diversos"
            :title="
              'Documentos diversos' +
              (fazenda.documentos_diversos_novos?.length > 0 ||
              fazenda.documentos_diversos?.length > 0
                ? ` (${
                    fazenda.documentos_diversos_novos.length +
                    (fazenda.documentos_diversos?.length || 0)
                  })`
                : '')
            "
            accept=".pdf,.doc,.docx,.xls,.xslx,.ppt,.pptx"
            helper="Envie os demais documentos."
            table="documentos_diversos"
          />
        </v-card>

        <v-card v-if="fazenda.tipo_imovel === 6" class="mt-6 mt-sm-8 form-card">
          <div class="position-relative">
            <TitleFormWidget icon="account-outline" title="Contatos" small />

            <v-btn
              @click="toggleDialogPeople('contact')"
              class="btn-new-people"
              color="primary"
              outlined
              dense
            >
              <v-icon color="primary" size="14">mdi-plus</v-icon>

              <div class="text-none ml-2">Novo Contato</div>
            </v-btn>
          </div>

          <div class="mt-3 mx-n3">
            <template v-if="fazenda.contacts.length">
              <PeopleSelect
                v-for="(item, index) in fazenda.contacts"
                :key="index + '-contacts'"
                @change="(v) => changePeople(index, v, 'contacts')"
                @delete="deletePeople(index, 'contacts')"
                :items="peoples"
                :has-selected="fazenda.contacts"
                :selected="item"
                type="contact"
              />
            </template>
          </div>

          <v-btn
            @click.stop="fazenda.contacts.push(null)"
            class="rounded-lg mt-3 btn-add-people"
            color="grey darken-2"
            outlined
            plain
          >
            <v-icon class="mr-1" size="12">mdi-plus</v-icon>
            <div class="text-caption text-none">Adicionar Contato</div>
          </v-btn>
        </v-card>

        <template v-else>
          <v-card class="mt-6 mt-sm-8 form-card">
            <div class="position-relative">
              <TitleFormWidget
                icon="account-outline"
                title="Proprietários"
                small
              />

              <v-btn
                @click="toggleDialogPeople('owner')"
                class="btn-new-people"
                color="primary"
                outlined
                dense
              >
                <v-icon color="primary" size="14">mdi-plus</v-icon>

                <div class="text-none ml-2">Novo Proprietário</div>
              </v-btn>
            </div>

            <div class="mt-3 mx-n3">
              <template v-if="fazenda.owners.length">
                <PeopleSelect
                  v-for="(item, index) in fazenda.owners"
                  :key="index + '-owners'"
                  @change="(v) => changePeople(index, v, 'owners')"
                  @delete="deletePeople(index, 'owners')"
                  :items="peoples"
                  :has-selected="fazenda.owners"
                  :selected="item"
                />
              </template>
            </div>

            <v-btn
              @click.stop="fazenda.owners.push(null)"
              class="rounded-lg mt-3 btn-add-people"
              color="grey darken-2"
              outlined
              plain
            >
              <v-icon class="mr-1" size="12">mdi-plus</v-icon>
              <div class="text-caption text-none">Adicionar Proprietário</div>
            </v-btn>
          </v-card>

          <v-card class="mt-6 mt-sm-8 form-card">
            <div class="position-relative">
              <TitleFormWidget icon="account-outline" title="Captador" small />

              <v-btn
                @click="toggleDialogPeople('captivator')"
                class="btn-new-people"
                color="primary"
                outlined
                dense
              >
                <v-icon color="primary" size="14">mdi-plus</v-icon>

                <div class="text-none ml-2">Novo Captador</div>
              </v-btn>
            </div>

            <div class="mt-3 mx-n3">
              <template v-if="fazenda.captivators.length">
                <PeopleSelect
                  v-for="(item, index) in fazenda.captivators"
                  :key="index + '-captivators'"
                  @change="(v) => changePeople(index, v, 'captivators')"
                  @delete="deletePeople(index, 'captivators')"
                  :items="peoples"
                  :has-selected="fazenda.captivators"
                  :selected="item"
                  type="captivator"
                />
              </template>
            </div>
          </v-card>
        </template>

        <v-sheet class="mt-6 mt-sm-8 fixed-bottom" color="#f7f8fa">
          <v-divider />

          <div class="d-flex justify-end py-4 px-6">
            <v-btn
              @click="dialogToGoBack = !dialogToGoBack"
              class="rounded-lg"
              color="#dddddd"
              outlined
            >
              <v-icon color="black" size="20">mdi-undo-variant</v-icon>
              <div
                class="black--text text-body-1 text-none font-weight-medium ml-2"
              >
                Voltar
              </div>
            </v-btn>

            <v-btn
              class="rounded-lg ml-2"
              color="orange"
              type="submit"
              depressed
            >
              <v-icon color="white" size="20">mdi-content-save</v-icon>
              <div
                class="white--text text-body-1 text-none font-weight-medium ml-2"
              >
                {{ itemId ? "Atualizar" : "Salvar" }}
              </div>
            </v-btn>
          </div>
        </v-sheet>
      </v-form>
    </template>

    <v-dialog
      v-if="dialogPeople"
      v-model="dialogPeople"
      max-width="600"
      persistent
    >
      <PeopleStoreDialog
        @close="toggleDialogPeople"
        @success="loadPeoples"
        :type="peopleType"
      />
    </v-dialog>

    <v-dialog
      v-if="dialogToGoBack"
      v-model="dialogToGoBack"
      max-width="500"
      persistent
    >
      <ToGoBackDialog @close="dialogToGoBack = !dialogToGoBack" />
    </v-dialog>
  </v-sheet>
</template>

<script>
import TitleFormWidget from "@/components/widgets/TitleForm.vue";
import TextFarmField from "@/components/fields/farm/Text.vue";
import SelectFarmField from "@/components/fields/farm/Select.vue";
import KmzFileFarmField from "@/components/fields/farm/KmzFile.vue";
import FileFarmField from "@/components/fields/farm/File.vue";
import TextareaFarmField from "@/components/fields/farm/Textarea.vue";
import PolygonMap from "@/components/PolygonMap.vue";
import PeopleSelect from "@/pages/portfolio/store/People.vue";
import PeopleStoreDialog from "@/components/dialogs/PeopleStore.vue";
import ToGoBackDialog from "@/components/dialogs/ToGoBack.vue";
import maskCpfCnpj from "@/utils/maskCpfCnpj";
import maskPhone from "@/utils/maskPhone";
import formatAreaUnit from "@/utils/formatAreaUnit";
import variables from "@/utils/variables";
import { mapActions } from "vuex";

export default {
  components: {
    TitleFormWidget,
    TextFarmField,
    SelectFarmField,
    KmzFileFarmField,
    FileFarmField,
    TextareaFarmField,
    PolygonMap,
    PeopleSelect,
    PeopleStoreDialog,
    ToGoBackDialog,
  },
  data: () => ({
    isLoading: false,
    dialogToGoBack: false,
    dialogPeople: false,
    peoples: [],
    peopleType: null,
    fazenda: {
      kmz: null,
      kmz_novo: null,
      nome: null,
      area_total: null,
      area_aberta: null,
      valor_total: null,
      tipo: null,
      cultura_desenv: null,
      benfeitorias: null,
      informacoes_diversas: null,
      documentos_ambientais_novos: [],
      documentos_fundiarios_novos: [],
      documentos_diversos_novos: [],
      fotos_novos: [],
      videos_novos: [],
      documentos_ambientais: [],
      documentos_fundiarios: [],
      documentos_diversos: [],
      contacts: [null],
      owners: [null],
      captivators: [null],
      fotos: [],
      videos: [],
      state_id: null,
      city_id: null,
      tipo_imovel: null,
    },
    states: [],
    citiesByState: [],
    isLoadingInsert: false,
    purpose: variables.purpose,
    areaType: variables.areaType,
    propertyType: variables.propertyType,
    areaUnit: variables.areaUnit,
  }),
  computed: {
    itemId() {
      return this.$route.params.id || null;
    },
    title() {
      if (this.fazenda.nome && this.fazenda.nome?.length > 2)
        return this.fazenda.nome;

      return !this.itemId ? "Nova Área" : this.fazenda.nome;
    },
    xsOnly() {
      return this.$vuetify.breakpoint.xsOnly;
    },
  },
  methods: {
    ...mapActions("states", ["listStates"]),
    ...mapActions("cities", ["listCitiesByStateID"]),
    ...mapActions("peoples", ["listPeoples"]),
    ...mapActions("properties", [
      "getProperty",
      "insertProperty",
      "updateProperty",
    ]),
    changePeople(index, id, type) {
      if (!["owners", "captivators", "contacts"].includes(type)) return;

      this.$set(this.fazenda[type], index, id);
    },
    deletePeople(index, type) {
      if (!["owners", "captivators", "contacts"].includes(type)) return;
      if (this.fazenda[type].length > 0 || this.fazenda[type][0] !== null)
        this.fazenda[type].splice(index, 1);
    },
    async updateState() {
      if (this.fazenda.state_id) {
        this.fazenda.city_id = null;
        let resp = await this.listCitiesByStateID(this.fazenda.state_id);

        this.citiesByState = resp.map(({ id, name }) => ({
          text: name,
          value: id,
        }));
      } else this.citiesByState = [];
    },
    async submit() {
      this.isLoadingInsert = true;

      const form = new FormData();

      for (let property in this.fazenda) {
        if (this.fazenda[property] || this.fazenda[property] == 0) {
          if (
            ![
              "contacts",
              "owners",
              "captivators",
              "kmz_novo",
              "documentos_ambientais_novos",
              "documentos_fundiarios_novos",
              "documentos_diversos_novos",
              "fotos_novos",
              "videos_novos",
              "kmz",
              "documentos_ambientais",
              "documentos_fundiarios",
              "documentos_diversos",
              "fotos",
              "videos",
            ].includes(property)
          ) {
            form.append(property, this.fazenda[property]);
          }
        }
      }

      if (this.fazenda.contacts.length > 0) {
        for (let item of this.fazenda.contacts) {
          form.append("contacts[]", item);
        }
      }

      if (this.fazenda.owners.length > 0) {
        for (let item of this.fazenda.owners) {
          form.append("owners[]", item);
        }
      }

      if (this.fazenda.captivators.length > 0) {
        for (let item of this.fazenda.captivators) {
          form.append("captivators[]", item);
        }
      }

      if (this.fazenda.kmz_novo) {
        form.append("kmz", this.fazenda.kmz_novo);
      }

      if (this.fazenda.documentos_ambientais_novos.length > 0) {
        for (let item of this.fazenda.documentos_ambientais_novos) {
          form.append("documentos_ambientais[]", item);
        }
      }

      if (this.fazenda.documentos_fundiarios_novos.length > 0) {
        for (let item of this.fazenda.documentos_fundiarios_novos) {
          form.append("documentos_fundiarios[]", item);
        }
      }

      if (this.fazenda.documentos_diversos_novos.length > 0) {
        for (let item of this.fazenda.documentos_diversos_novos) {
          form.append("documentos_diversos[]", item);
        }
      }

      if (this.fazenda.fotos_novos.length > 0) {
        for (let item of this.fazenda.fotos_novos) {
          form.append("fotos[]", item);
        }
      }

      if (this.fazenda.videos_novos.length > 0) {
        for (let item of this.fazenda.videos_novos) {
          form.append("videos[]", item);
        }
      }

      const action = this.itemId ? this.updateProperty : this.insertProperty;
      const r = await action.call(this, form);
      if (r) {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-map-marker-check",
          message: "Área cadastrada com sucesso.",
          type: "success",
        });
        this.$router.push({ path: "/portfolio" });
      } else {
        this.$eventHub.$emit("snackBar", {
          icon: "mdi-map-marker-alert",
          message: "Não foi possível cadastrar a área. Tente novamente.",
          type: "error",
        });
      }

      this.isLoadingInsert = false;
    },
    deleteFiles(params) {
      const type = params.type;
      const ids = params.ids;

      if (!type || !ids || !ids.length > 0) return;

      this.fazenda[type] = this.fazenda[type].filter(
        (item) => !ids.includes(item.id),
      );
    },
    async fillCity() {
      if (this.fazenda.state_id) {
        let resp = await this.listCitiesByStateID(this.fazenda.state_id);

        this.citiesByState = resp.map(({ id, name }) => ({
          text: name,
          value: id,
        }));
      }
    },
    async loadPeoples() {
      this.peoples = await this.listPeoples({ all: 1 });
      this.toggleDialogPeople();
    },
    async onLoad() {
      this.isLoading = true;
      this.peoples = await this.listPeoples({ all: 1 });

      if (!this.itemId) {
        this.isLoading = false;
        return;
      }

      this.fazenda = await this.getProperty(this.itemId);

      this.fazenda.kmz_novo = null;
      this.fazenda.fotos_novos = [];
      this.fazenda.videos_novos = [];
      this.fazenda.documentos_ambientais_novos = [];
      this.fazenda.documentos_fundiarios_novos = [];
      this.fazenda.documentos_diversos_novos = [];

      if (this.fazenda.contacts.length === 0) this.fazenda.contacts.push(null);
      if (this.fazenda.owners.length === 0) this.fazenda.owners.push(null);
      if (this.fazenda.captivators.length === 0)
        this.fazenda.captivators.push(null);

      this.fillCity();
      this.isLoading = false;
    },
    toggleDialogPeople(type = null) {
      this.peopleType = type || "contact";

      this.dialogPeople = !this.dialogPeople;
    },
    maskCpfCnpj,
    maskPhone,
    formatAreaUnit,
  },
  watch: {
    "fazenda.owners": {
      handler(v) {
        if (!v || v.length === 0) {
          this.fazenda.owners.push(null);
        }
      },
    },
    "fazenda.captivators": {
      handler(v) {
        if (!v || v.length === 0) {
          this.fazenda.captivators.push(null);
        }
      },
    },
  },
  async mounted() {
    this.states = await this.listStates();
  },
  created() {
    this.onLoad();
  },
};
</script>

<style lang="scss" scoped>
$gap: 12px;

.form-card {
  border: 1px solid #e5e5e5 !important;
  border-radius: 14px !important;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1) !important;
  padding: 24px;
}

.grid-list {
  display: grid;
  gap: $gap;
}

.grid-list-state-city {
  grid-column: span 2;
  grid-template-columns: 1fr 1fr;
}

.grid-list-4-columns {
  $columns: 4;

  display: grid;
  gap: $gap;
  grid-template-columns: repeat(
    $columns,
    calc(25% - ($gap * ($columns - 1) / $columns))
  );
}

.grid-list-5-columns {
  $columns: 5;

  display: grid;
  gap: $gap;
  grid-template-columns: repeat(
    $columns,
    calc(20% - ($gap * ($columns - 1) / $columns))
  );
}

.grid-list-additional-information {
  grid-template-columns: 1fr 1fr;
}

.grid-list-files {
  grid-template-columns: 1fr 1fr;
}

.span2 {
  grid-column: span 2;
}

.span3 {
  grid-column: span 3;
}

.span4 {
  grid-column: span 4;
}

.fixed-top {
  top: 64px;
  margin-bottom: -1px;
  margin-left: -12px;
  margin-right: -12px;
  position: sticky;
  z-index: 2;

  @media screen and (max-width: 959.9px) {
    top: 56px;
  }
}

.fixed-bottom {
  bottom: 0;
  margin-left: -12px;
  margin-right: -12px;
  position: sticky;
}

.btn-new-people {
  border: 1px dashed rgba(16, 120, 35, 0.3) !important;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

.btn-add-people {
  border-style: dashed !important;
}
</style>
